<template>
  <el-dialog
    :visible="true"
    :fullscreen="true"
    :close-on-click-modal="false"
    :before-close="handleClose"
  >
    <div slot="title">
      {{ title }}
      <el-tag
        v-if="supplierReturnOrder.status && (type === 'update' || type === 'view') && !loading"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px;"
      >
        {{ getStatus().label }}
      </el-tag>
    </div>
    <div v-if="type === 'view' && supplierReturnOrder.receipt_number">
      <el-link @click="openReceipt" type="primary" style="margin-bottom: 30px; margin-top: -20px;">
        <i class="el-icon-printer el-icon--right"></i>
        {{ supplierReturnOrder.receipt_number }}
      </el-link>
    </div>
    <div v-loading="loading">
      <el-form
        ref="orderForm"
        label-position="top"
        :model="supplierReturnOrder"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="supplier"
              label="Szállító"
            >
              <el-select
                v-model="supplierReturnOrder.supplier"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="supplierReturnOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="supplier in suppliers"
                  :key="supplier.id + 'supplier'"
                  :value="supplier"
                  :label="supplier.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="customer"
              label="Átvevő"
            >
              <el-select
                v-model="supplierReturnOrder.customer"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="supplierReturnOrder.status !== 'recorded' || type === 'view'"
                @change="onCustomerChange"
              >
                <el-option
                  v-for="customer in customers"
                  :key="customer.id + 'supplier'"
                  :value="customer"
                  :label="customer.company.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="`Ebből a raktárból küldjük vissza az árut ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                  placement="right"
                >
                  <i class="el-icon-info"/>
                </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="supplierReturnOrder.storage"
                style="width: 100%;"
                value-key="id"
                :disabled="supplierReturnOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="receipt_prefix"
              label="Bizonylat előtag"
            >
              <el-input
                v-model="receiptPrefix"
                disabled
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="cm_delivery_note_number"
              label="CM szlev száma"
            >
              <el-input
                v-model="supplierReturnOrder.cm_delivery_note_number"
                :disabled="type === 'update' && supplierReturnOrder.status !== 'recorded'"
              />
            </el-form-item>
          </el-col>

          <el-col :span="6">
            <el-form-item
              prop="currency_id"
              label="Deviza"
            >
              <el-select
                v-model="supplierReturnOrder.currency"
                :disabled="supplierReturnOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="currency in currencies"
                  :key="currency.id + 'currency'"
                  :value="currency"
                  :label="currency.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="completed_at"
              label="Teljesítés dátuma"
            >
              <el-date-picker
                v-model="supplierReturnOrder.completed_at"
                type="date"
                :disabled="supplierReturnOrder.status === 'done' || type === 'view'"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="reference_number"
              label="Hivatkozási szám"
            >
              <el-input
                v-model="supplierReturnOrder.reference_number"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col>
            <el-form-item
              label=""
              prop="is_stockist"
              style="margin-bottom: 0"
            >
              <el-checkbox
                v-model="supplierReturnOrder.is_stockist"
                :disabled="type === 'view'"
              >
                Beszerzési árak véglegesek
              </el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>
        <product-items
          ref="productItems"
          :default-items="supplierReturnOrder.order_items"
          :storage-id="supplierReturnOrder.storage.id"
          :currency="supplierReturnOrder.currency.code"
          :default-supplier="type === 'add' && supplierReturnOrder.customer || null"
          :enabled-elements="enabledElements"
          :enabled-columns="type === 'view' ? ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'supplierSelector'] : ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'actions', 'supplierSelector']"
          :available-products="products"
          compare-type="completion"
          :compare-items="supplierReturnOrder.inward_handling && supplierReturnOrder.inward_handling.inward_handling_items"
          :is-vat-zero="supplierReturnOrder.customer && supplierReturnOrder.customer.supplier_category && supplierReturnOrder.customer.supplier_category.value === 'Külföldi szállító'"
          @priceChange="onPriceChange"
        />
      </el-form>
      <div class="price-sum">
        <div>Nettó: <strong>{{ supplierReturnOrder.net_price | currency(supplierReturnOrder.currency.code) }}</strong></div>
        <div style="margin-left: 20px;">
          Bruttó: <strong>{{ supplierReturnOrder.gross_price | currency(supplierReturnOrder.currency.code) }}</strong>
        </div>
      </div>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        v-if="supplierReturnOrder.status !== 'recorded' && supplierReturnOrder.receipt_file"
        @click="openReceipt"
      >
        Bizonylat nyomtatása
      </el-button>
      <el-button
        type="primary"
        :disabled="supplierReturnOrder.status === 'cancelled'"
        :loading="saveButtonLoading"
        @click="store"
      >
        Mentés
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import ProductItems from '../../utils/ProductItems/index';

export default {
  components: {
    ProductItems,
  },
  props: ['type', 'supplierReturnOrderId'],
  data() {
    return {
      loading: false,
      buttonLoading: false,
      saveButtonLoading: false,
      receiptPrefix: '',
      products: null,
      supplierReturnOrder: {
        status: 'recorded',
        net_price: 0,
        gross_price: 0,
        order_items: [],
        reference_number: '',
        completed_at: null,
        storage: {},
        customer: null,
        supplier: {},
        currency: {},
        is_stockist: false,
        cm_delivery_note_number: '',
      },
      rules: {
        customer: [
          {
            required: true,
            message: 'Vevő kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        supplier: [
          {
            required: true,
            message: 'Beszállító kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        cm_delivery_note_number: [
          {
            max: 255,
            message: 'A CM szállítólevél szám maximális hossza 255 karakter',
            trigger: 'blur'
          }
        ],
      },
    };
  },
  computed: {
    title() {
      if (this.type === 'add') {
        return 'Új beszállítói visszáru';
      } else if (this.type === 'update') {
        return 'Beszállítói visszáru módosítása';
      } else if (this.type === 'view') {
        return 'Beszállítói visszáru megtekintése';
      } else {
        return '';
      }
    },
    buttonText() {
      if (this.type === 'add') {
        return 'Hozzáadása';
      } else if (this.type === 'update') {
        return 'Módosítás';
      } else {
        return '';
      }
    },
    enabledElements() {
      if(this.type === 'view') {
        return [];
      }
      switch (this.supplierReturnOrder.status) {
        case 'recorded':
          return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'expirationDateSelector', 'supplierSelector'];
        case 'sent':
          return ['count', 'netPrice', 'vat', 'unitPrice'];
        case 'cancelled':
          return [];
        case 'done':
          return this.supplierReturnOrder.is_stockist ? ['netPrice', 'vat'] : ['unitPrice', 'netPrice', 'vat'];
        default:
          return [];
      }
    },
    suppliers() {
      return this.$store.getters['ownCompany/ownCompanies'];
    },
    customers() {
      return this.$store.getters['partner/suppliers'];
    },
    storages() {
      return this.$store.getters['storage/ownStorages'];
    },
    currencies() {
      return this.$store.getters['currency/currencies'];
    },
  },
  async mounted() {
    this.loading = true;
    const requests = [];
    if(!this.suppliers || !this.suppliers.length){
      requests.push(this.$store.dispatch('ownCompany/fetchAll', ''));
    }
    if(!this.customers || !this.customers.length){
      requests.push(this.$store.dispatch('partner/fetchSuppliers', ''));
    }
    if(!this.storages || !this.storages.length){
      requests.push(this.$store.dispatch('storage/fetchAll', ''));
    }
    if(!this.currencies || !this.currencies.length){
      requests.push(this.$store.dispatch('currency/fetchAll'));
    }
    if(!this.$store.getters['vat/vats'] || !this.$store.getters['vat/vats'].length){
      requests.push(this.$store.dispatch('vat/fetchAll'));
    }
    await Promise.all(requests);
    this.receiptPrefix = (await axios.get('api/receipt-prefixes/supplierReturnOrder')).full_prefix;
    if (this.supplierReturnOrderId) {
      this.supplierReturnOrder = await this.$store.dispatch('supplierReturnOrder/fetch', this.supplierReturnOrderId);
      this.products = await this.$store.dispatch('product/fetchBySupplierId', this.supplierReturnOrder.customer.id);
      if(this.type === 'add') {
        this.supplierReturnOrder.status = 'recorded';
        this.supplierReturnOrder.receipt_number = null;
        this.supplierReturnOrder.order_items = this.supplierReturnOrder.order_items.map(item => {
          delete item.id;
          return item;
        });
      }
    } else {
      this.supplierReturnOrder.supplier = this.suppliers[0];
      this.supplierReturnOrder.currency = this.currencies[0];
      this.supplierReturnOrder.storage = this.storages[0];
      this.supplierReturnOrder.completed_at = moment()
        .format('Y-MM-DD');
    }
    this.loading = false;
  },
  methods: {
    async onCustomerChange() {
      this.$refs['productItems'].clearItems();
      this.loading = true;
      this.products = await this.$store.dispatch('product/fetchBySupplierId', this.supplierReturnOrder.customer.id);
      this.loading = false;
    },
    onPriceChange({ netPrice, grossPrice }) {
      this.supplierReturnOrder.net_price = netPrice;
      this.supplierReturnOrder.gross_price = grossPrice;
    },
    handleClose() {
      if(this.type === 'view') {
        return this.$router.replace('/processes/supplier-return-orders');
      }
      this.$confirm('Biztosan elveted a módosításokat?', {
        confirmButtonText: 'Igen',
        type: 'warning',
      })
        .then(() => {
          this.$router.replace('/processes/supplier-return-orders');
        })
        .catch(() => {
        });
    },
    async cancel() {
      this.cancelButtonLoading = true;
      await this.$store.dispatch('supplierReturnOrder/cancel', this.supplierReturnOrder.id);
      this.loading = true;
      await this.$store.dispatch('supplierReturnOrder/fetchAll');
      this.supplierReturnOrder = await this.$store.dispatch('supplierReturnOrder/fetch', this.supplierReturnOrderId);
      this.loading = false;
      this.cancelButtonLoading = false;
    },
    async store() {
      this.$refs.orderForm.validate(async valid => {
        if (valid) {
          this.supplierReturnOrder.order_items = this.$refs['productItems'].getItems();
          if (!this.supplierReturnOrder.order_items.length) {
            return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
          } else if (!this.$refs['productItems'].validateProducts()) {
            return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
          }
          this.saveButtonLoading = true;
          try {
            if (this.type === 'add') {
              await this.$store.dispatch('supplierReturnOrder/create', this.supplierReturnOrder);
            } else if (this.type === 'update') {
              await this.$store.dispatch('supplierReturnOrder/update', this.supplierReturnOrder);
            }
            this.$store.dispatch('supplierReturnOrder/fetchAll');
            this.saveButtonLoading = false;
            this.$router.replace('/processes/supplier-return-orders');
          } catch (e) {
          }
          this.saveButtonLoading = false;
        }
      });
    },
    openReceipt() {
      if (this.supplierReturnOrder.receipt_file && this.supplierReturnOrder.receipt_file.path) {
        window.open(this.supplierReturnOrder.receipt_file.path);
      }
    },
    getStatus() {
      switch (this.supplierReturnOrder.status) {
        case 'recorded':
          return {
            label: 'Rögzítve',
            tag: 'info',
          };
        case 'done':
          return {
            label: 'Visszaküldve',
            tag: 'success',
          };
      }
    },
  },
};
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;
    > div {
      display: inline-block;
    }
  }
</style>
